/**
 * /admin/courses/$courseId/weeks/$weekId — one week: what it is about, what
 * it produces, what it builds in NOVA, its class video, whether the trial
 * opens it, and its materials in order. Saving replaces the whole material
 * list.
 *
 * Aliases: week editor, course week admin, course materials editor
 */

import { useStore } from "@tanstack/react-form";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
  useQuery,
} from "@tanstack/react-query";
import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import { useId, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { VideoPicker, type PickerVideo } from "~/components/admin/video-picker";
import { useAppForm } from "~/components/form/app-form";
import { useFieldContext } from "~/components/form/fields/field-context";
import {
  FieldErrorsProvider,
  useFieldError,
  zodErrorLookup,
} from "~/components/form/fields/field-errors";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { FormField } from "~/components/ui/form-field";
import {
  courseQueryOptions,
  learningAdminKeys,
  updateCourseWeekFn,
  videosQueryOptions,
  type AdminCourse,
  type AdminCourseWeek,
  type LibraryVideo,
} from "~/lib/functions/admin-learning";
import {
  courseWeekInputSchema,
  type CourseMaterialInput,
} from "~/lib/shared/learning/admin-inputs";
import {
  COURSE_MATERIAL_DEFAULT_NOTES,
  COURSE_MATERIAL_KIND_LABELS,
  COURSE_MATERIAL_KINDS,
  type CourseMaterialKind,
} from "~/lib/shared/learning/courses";
import type { VideoPickerContext } from "~/lib/shared/learning/video-usage";
import {
  COURSE_BUILDABLE_ASSETS,
  type AssetKey,
} from "~/lib/shared/nova/assets/catalog";

export const Route = createFileRoute("/admin/courses/$courseId/weeks/$weekId")({
  staticData: { title: "Week" },
  loader: async ({ context, params }) => {
    if (
      !z.string().uuid().safeParse(params.courseId).success ||
      !z.string().uuid().safeParse(params.weekId).success
    ) {
      throw notFound();
    }
    const [course] = await Promise.all([
      context.queryClient.ensureQueryData(courseQueryOptions(params.courseId)),
      context.queryClient.ensureQueryData(videosQueryOptions()),
    ]);
    const week = course?.weeks.find((w) => w.id === params.weekId);
    if (!week) throw notFound();
    return { title: `Week ${week.position}: ${week.title}` };
  },
  component: WeekEditorRoute,
});

function WeekEditorRoute() {
  const { courseId, weekId } = Route.useParams();
  const { data: course } = useSuspenseQuery(courseQueryOptions(courseId));
  const { data: videos = [] } = useQuery(videosQueryOptions());
  const week = course?.weeks.find((w) => w.id === weekId);
  if (!course || !week) throw notFound();
  return (
    <WeekEditor course={course} key={week.id} videos={videos} week={week} />
  );
}

/** The chosen keys after one checkbox changes, kept in catalog order. */
function toggleAsset(
  chosen: readonly AssetKey[],
  key: AssetKey,
  checked: boolean,
): AssetKey[] {
  return COURSE_BUILDABLE_ASSETS.map((asset) => asset.key).filter(
    (candidate) => (candidate === key ? checked : chosen.includes(candidate)),
  );
}

interface MaterialRow {
  kind: CourseMaterialKind;
  title: string;
  note: string;
  url: string;
  videoId: string;
}

const EMPTY_MATERIAL: MaterialRow = {
  kind: "template",
  title: "",
  note: "",
  url: "",
  videoId: "",
};

/** The form's flat rows as the input the server validates. */
function toMaterialInput(row: MaterialRow): CourseMaterialInput {
  return row.kind === "replay"
    ? {
        kind: "replay",
        title: row.title,
        note: row.note,
        videoId: row.videoId,
      }
    : { kind: row.kind, title: row.title, note: row.note, url: row.url };
}

/**
 * A video picker bound to a form field. An empty string is "no video", as the
 * material rows store it.
 */
function VideoPickerField({
  label,
  helpText,
  required,
  context,
  videos,
  noneLabel,
  placeholder,
}: {
  label: string;
  helpText?: string;
  required?: boolean;
  context: VideoPickerContext;
  videos: readonly PickerVideo[];
  noneLabel?: string;
  placeholder: string;
}) {
  const field = useFieldContext<string>();
  const error = useFieldError(field.name);
  const id = useId();
  return (
    <FormField
      error={error}
      helpText={helpText}
      inputId={id}
      label={label}
      required={required}
    >
      <VideoPicker
        context={context}
        id={id}
        invalid={error !== undefined}
        label={label}
        noneLabel={noneLabel}
        onChange={(videoId) => field.handleChange(videoId ?? "")}
        placeholder={placeholder}
        value={field.state.value === "" ? null : field.state.value}
        videos={videos}
      />
    </FormField>
  );
}

function WeekEditor({
  course,
  week,
  videos,
}: {
  course: AdminCourse;
  week: AdminCourseWeek;
  videos: readonly LibraryVideo[];
}) {
  const courseId = course.id;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const pickerContext: VideoPickerContext = {
    kind: "course",
    courseId: course.id,
    courseTitle: course.title,
  };

  const form = useAppForm({
    defaultValues: {
      title: week.title,
      summary: week.summary,
      outcome: week.outcome,
      classVideoId: week.classVideoId ?? "",
      producesAssets: [...week.producesAssets],
      materials: week.materials.map((material): MaterialRow => ({
        kind: material.kind,
        title: material.title,
        note: material.note ?? "",
        url: material.url ?? "",
        videoId: material.videoId ?? "",
      })),
    },
  });
  const values = useStore(form.store, (state) => state.values);
  const parsed = courseWeekInputSchema.safeParse({
    ...values,
    classVideoId: values.classVideoId === "" ? null : values.classVideoId,
    materials: values.materials.map(toMaterialInput),
  });
  const errorFor = zodErrorLookup(
    parsed.success ? [] : parsed.error.issues,
    submitAttempted,
  );

  const save = useMutation({
    mutationFn: updateCourseWeekFn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: learningAdminKeys.all });
      toast.success(`Week ${week.position} saved.`);
      await navigate({
        to: "/admin/courses/$courseId",
        params: { courseId },
      });
    },
    onError: () => toast.error("Couldn't save the week."),
  });

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-medium text-gray-900">
        Week {week.position}
      </h1>

      <FieldErrorsProvider value={errorFor}>
        <form
          className="space-y-6"
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitAttempted(true);
            if (parsed.success) {
              save.mutate({ data: { ...parsed.data, id: week.id } });
            }
          }}
        >
          <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-5">
            <form.AppField name="title">
              {(field) => <field.TextField label="Title" required />}
            </form.AppField>
            <form.AppField name="summary">
              {(field) => (
                <field.TextareaField
                  helpText="One line under the title on the course page."
                  label="Summary"
                  rows={2}
                />
              )}
            </form.AppField>
            <form.AppField name="outcome">
              {(field) => (
                <field.TextareaField
                  helpText="Shown as “What this week produces”, in the coach's terms."
                  label="What this week produces"
                  rows={2}
                />
              )}
            </form.AppField>
            <form.AppField name="classVideoId">
              {() => (
                <VideoPickerField
                  context={pickerContext}
                  helpText="Later weeks can be templates and practice, with no class."
                  label="Class video"
                  noneLabel="No class video"
                  placeholder="No class video"
                  videos={videos}
                />
              )}
            </form.AppField>
          </section>

          <form.Field name="producesAssets">
            {(field) => (
              <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-5">
                <div className="space-y-1">
                  <h2 className="font-medium text-gray-900" id="week-builds">
                    What this week builds in NOVA
                  </h2>
                  <p className="text-text-secondary text-sm">
                    Approving all of them completes the week and opens the next.
                    Leave empty and the coach marks the week complete herself.
                  </p>
                </div>
                <div
                  aria-labelledby="week-builds"
                  className="grid grid-cols-1 gap-2 sm:grid-cols-2"
                  role="group"
                >
                  {COURSE_BUILDABLE_ASSETS.map((asset) => (
                    <Checkbox
                      checked={field.state.value.includes(asset.key)}
                      key={asset.key}
                      onCheckedChange={(checked) =>
                        field.handleChange(
                          toggleAsset(
                            field.state.value,
                            asset.key,
                            checked === true,
                          ),
                        )
                      }
                    >
                      {asset.label}
                    </Checkbox>
                  ))}
                </div>
              </section>
            )}
          </form.Field>

          <section className="space-y-3">
            <div className="space-y-1">
              <h2 className="font-medium text-gray-900">Materials</h2>
              <p className="text-text-secondary text-sm">
                Slides and templates are links, such as a PDF or a Google Doc
                anyone with the link can open. A replay is a video from the
                library.
              </p>
            </div>

            {values.materials.map((material, index) => (
              <div
                className="space-y-3 rounded-xl border border-gray-200 bg-white p-4"
                key={index}
              >
                <div className="flex flex-wrap items-start gap-3">
                  <div className="w-40 flex-none">
                    <form.AppField name={`materials[${index}].kind`}>
                      {(field) => (
                        <field.SelectField
                          label="Kind"
                          options={COURSE_MATERIAL_KINDS.map((kind) => ({
                            value: kind,
                            label: COURSE_MATERIAL_KIND_LABELS[kind],
                          }))}
                        />
                      )}
                    </form.AppField>
                  </div>
                  <div className="min-w-0 flex-1 basis-56">
                    <form.AppField name={`materials[${index}].title`}>
                      {(field) => <field.TextField label="Title" required />}
                    </form.AppField>
                  </div>
                  <div className="flex gap-1 pt-6">
                    <Button
                      aria-label="Move up"
                      disabled={index === 0}
                      onClick={() =>
                        form.moveFieldValues("materials", index, index - 1)
                      }
                      size="icon-sm"
                      type="button"
                      variant="ghost"
                    >
                      <ArrowUp className="size-4" />
                    </Button>
                    <Button
                      aria-label="Move down"
                      disabled={index === values.materials.length - 1}
                      onClick={() =>
                        form.moveFieldValues("materials", index, index + 1)
                      }
                      size="icon-sm"
                      type="button"
                      variant="ghost"
                    >
                      <ArrowDown className="size-4" />
                    </Button>
                    <Button
                      aria-label="Remove"
                      onClick={() => form.removeFieldValue("materials", index)}
                      size="icon-sm"
                      type="button"
                      variant="destructive-ghost"
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {material.kind === "replay" ? (
                    <form.AppField name={`materials[${index}].videoId`}>
                      {() => (
                        <VideoPickerField
                          context={pickerContext}
                          label="Replay video"
                          placeholder="Choose a library video"
                          required
                          videos={videos}
                        />
                      )}
                    </form.AppField>
                  ) : (
                    <form.AppField name={`materials[${index}].url`}>
                      {(field) => (
                        <field.TextField
                          label="Link"
                          placeholder="https://…"
                          required
                          type="url"
                        />
                      )}
                    </form.AppField>
                  )}
                  <form.AppField name={`materials[${index}].note`}>
                    {(field) => (
                      <field.TextField
                        label="Line under the title"
                        placeholder={
                          COURSE_MATERIAL_DEFAULT_NOTES[material.kind]
                        }
                      />
                    )}
                  </form.AppField>
                </div>
              </div>
            ))}

            <div className="flex flex-wrap gap-2">
              {COURSE_MATERIAL_KINDS.map((kind) => (
                <Button
                  key={kind}
                  onClick={() =>
                    form.pushFieldValue("materials", {
                      ...EMPTY_MATERIAL,
                      kind,
                    })
                  }
                  size="sm"
                  type="button"
                >
                  <Plus aria-hidden="true" className="size-4" />
                  {kind === "replay"
                    ? "Office hours replay"
                    : COURSE_MATERIAL_KIND_LABELS[kind]}
                </Button>
              ))}
            </div>
          </section>

          <div className="flex gap-2">
            <Button loading={save.isPending} type="submit" variant="primary">
              Save week
            </Button>
            <Button
              onClick={() =>
                navigate({
                  to: "/admin/courses/$courseId",
                  params: { courseId },
                })
              }
              type="button"
              variant="ghost"
            >
              Cancel
            </Button>
          </div>
        </form>
      </FieldErrorsProvider>
    </div>
  );
}
